<template>
  <div class="html-editor">
    <b-modal
      size="color"
      hide-header
      hide-backdrop
      body-class="color"
      @hide="hide"
      ref="colorModal"
    >
      <chrome v-model="color" />
    </b-modal>
    <b-modal
      centered
      body-class="text-center"
      footer-class="justify-content-center"
      hide-header
      ok-only
      ref="size"
    >
      <p>
        이미지당 용량이 10MB를 넘을 수 없습니다.<br />
        용량을 조절한 후 다시 시도해주세요.
      </p>
      <p>
        You can not upload images larger than 10MB.<br />
        Reduce file size and try again.
      </p>
    </b-modal>

    <textarea :id="this.id"></textarea>
  </div>
</template>

<script>
import tinymce from "tinymce/tinymce";
import "tinymce/skins/lightgray/skin.min.css";
import "tinymce/themes/modern/theme";
import "tinymce/plugins/paste";
import "tinymce/plugins/textcolor";
import "tinymce/plugins/code";
import "tinymce/plugins/link";
import "tinymce/plugins/media";
import "tinymce/plugins/hr";
import { Chrome } from "vue-color";

export default {
  components: {
    Chrome
  },
  props: {
    id: { required: true },
    value: { required: true },
    backgroundColor: { required: true }
  },
  data() {
    return {
      editor: null,
      color: "white",
      currentColor: "white"
    };
  },
  watch: {
    value(val) {
      if (val === null) {
        return;
      }

      const content = this.editor.getContent();
      if (val !== content) {
        this.editor.setContent(val);
      }
    },
    backgroundColor() {
      this.color = this.currentColor = this.backgroundColor;
    },
    color() {
      this.setColor(this.color);
    }
  },
  mounted() {
    this.color = this.currentColor = this.backgroundColor;

    tinymce.init({
      height: 514,
      selector: `#${this.id}`,
      plugins: ["paste", "textcolor", "code", "link", "media", "hr"],
      menubar: false,
      toolbar:
        "bold italic underline strikethrough | fontselect | fontsizeselect | forecolor | alignleft aligncenter alignright | code link | image media hr | background",
      setup: editor => {
        this.editor = editor;

        this.editor.on("init", () => {
          this.editor.setContent(this.value);
        });

        this.editor.on("change keyup undo redo", () => {
          this.$emit("input", this.editor.getContent());
        });

        this.editor.addButton("image", {
          icon: "image",
          onclick: () => {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");
            input.setAttribute("multiple", true);
            input.onchange = async event => {
              const { files } = event.target;
              this.$emit("createId", async () => {
                await Promise.all(
                  Array.from(files).map(async file =>
                    this.insertImage(await this.getBlobUri(file))
                  )
                );
                this.$emit("autoSave");
              });
            };

            input.click();
          }
        });

        this.editor.addButton("background", {
          icon: "background",
          onclick: function() {
            this.color = this.currentColor;
            this.$refs.colorModal.show();
          }.bind(this)
        });
      },
      init_instance_callback: () => {
        this.setColor(this.currentColor);
      },
      content_css: [
        "//fonts.googleapis.com/css?family=Josefin+Sans%7CNoto+Sans+KR:400,700",
        "//stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css"
      ],
      content_style:
        "body { font-family: Arial; padding: 0 40px; } hr { width: 80%; } hr:first-child { margin-top: 0 } hr:last-child { margin-bottom: 0 } iframe { max-width: 100%; border: none; } .no-padding { max-width: calc(100% + 80px); margin: 0 -40px; } .img-percent { display: block; margin: 0 auto !important; @media only screen and (max-width: 900px) { width: 900px !important; } }",
      font_formats:
        "본고딕=Noto Sans KR;Josefin Sans=josefin sans;Andale Mono=andale mono,times;Arial=arial,helvetica,sans-serif;Arial Black=arial black,avant garde;Book Antiqua=book antiqua,palatino;Comic Sans MS=comic sans ms,sans-serif;Courier New=courier new,courier;Georgia=georgia,palatino;Helvetica=helvetica;Impact=impact,chicago;Symbol=symbol;Tahoma=tahoma,arial,helvetica,sans-serif;Terminal=terminal,monaco;Times New Roman=times new roman,times;Trebuchet MS=trebuchet ms,geneva;Verdana=verdana,geneva;Webdings=webdings;Wingdings=wingdings,zapf dingbats",
      object_resizing: false,
      paste_data_images: true,
      paste_preprocess: (plugin, args) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(args.content, "text/html");
        const node = doc.body.firstChild;
        if (node.nodeName !== "IMG") {
          return;
        }

        args.content = "";
        this.insertImage(node.getAttribute("src"));
      },
      automatic_uploads: false,
      images_reuse_filename: true,
      convert_urls: false,
      images_upload_handler: (blobInfo, success, failure) => {
        this.$emit(
          "upload",
          blobInfo.filename(),
          blobInfo.blob(),
          success,
          failure
        );
      },
      forced_root_block: false
    });
  },
  beforeDestroy() {
    tinymce.get(`${this.id}`).remove();
  },
  methods: {
    getBlobUri(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const base64 = reader.result.split(",")[1];
          const blobCache = this.editor.editorUpload.blobCache;
          const existingBlobInfo = blobCache.findFirst(
            cachedBlobInfo => cachedBlobInfo.base64() === base64
          );

          let blobInfo = null;
          if (!existingBlobInfo) {
            blobInfo = blobCache.create("", file, base64, file.name);
            blobCache.add(blobInfo);
          } else {
            blobInfo = existingBlobInfo;
          }

          resolve(blobInfo.blobUri());
        };
        reader.onerror = () => {
          reject();
        };

        reader.readAsDataURL(file);
      });
    },
    async save() {
      await this.editor.uploadImages();
      this.$emit("input", this.editor.getContent());
    },
    setColor(color) {
      if (!this.$el.querySelector("iframe")) {
        return;
      }

      const body = this.$el.querySelector("iframe").contentWindow.document.body;
      body.style.backgroundColor = color.hex || color;
    },
    hide(event) {
      if (event.trigger === "ok") {
        this.currentColor = this.color;
        this.$emit("changeBackgroundColor", this.currentColor);
      } else {
        this.color = this.currentColor;
      }
    },
    async insertImage(src) {
      return new Promise(async (resolve, reject) => {
        if (src.startsWith("blob:")) {
          const size = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("GET", src, true);
            xhr.responseType = "blob";
            xhr.onload = () => {
              if (xhr.status !== 200) {
                reject();
                return;
              }

              resolve(xhr.response.size);
            };
            xhr.onerror = () => {
              reject();
            };
            xhr.send();
          });

          if (size > 10485760) {
            this.$refs.size.show();
            reject();
            return;
          }
        }

        const parser = new DOMParser();
        let doc = parser.parseFromString(`<img src="${src}">`, "text/html");
        const node = doc.body.firstChild;
        const image = new Image();
        image.onload = () => {
          node.classList.add("img-fluid");
          if (image.width > 900 && image.width < 1400) {
            node.style.width = `${parseInt(image.width / 13.2)}%`;
            node.classList.add("img-percent");
          } else if (image.width >= 1400) {
            doc = parser.parseFromString(
              `<img src="${src}" class="no-padding"><br><br>`,
              "text/html"
            );
          }

          this.editor.insertContent(doc.body.innerHTML);
          resolve();
        };
        image.src = src;
      });
    }
  }
};
</script>

<style lang="scss">
.mce-menu {
  z-index: 1000 !important;
}
</style>

<style lang="scss">
// TinyMCE renders its UI outside Vue, and b-modal renders into <body>, so
// these are namespaced instead of scoped.
.html-editor .mce-menubtn {
  z-index: 999 !important;
}

.html-editor .mce-tinymce {
  background-color: transparent;
  border: none;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;

  .mce-top-part {
    margin-bottom: 5px;

    &::before {
      -webkit-box-shadow: none;
      -moz-box-shadow: none;
      box-shadow: none;
    }
  }

  .mce-edit-area {
    border: none;
  }

  .mce-top-part,
  .mce-edit-area {
    overflow: hidden;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }

  .mce-top-part,
  .mce-statusbar {
    overflow: hidden;
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  .mce-toolbar .mce-container-body {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .mce-btn-group:nth-child(7) {
      flex: 1;

      div {
        display: flex;
        justify-content: center;
      }
    }
  }
}

.modal-dialog.modal-color {
  width: 260px;
}
</style>

<style lang="scss" scoped>
.uploader {
  border: 2px dashed #0087f7;
  border-radius: 5px;
  padding-top: 100%;

  div {
    font-size: 20px;
    margin-top: -50%;
  }
}
</style>
